"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Code as Code2, Compass, Layers, ArrowRight, TrendingUp, Cpu, Target, CircleCheck as CheckCircle2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const strategies = [{
  id: "product-led",
  title: "Product-led content architecture",
  tag: "Search Architecture",
  icon: Compass,
  summary: "Instead of writing top-of-funnel listicles that attract passive traffic, I help you build pages around the precise jobs your software solves.",
  points: ["Target high-intent comparison and 'alternative to' search terms", "Structure programmatic feature use-case landing pages", "Map user workflows directly to active search intent"]
}, {
  id: "technical-js",
  title: "Technical SEO for JS-heavy web apps",
  tag: "Technical SEO",
  icon: Code2,
  summary: "Modern React, Next.js, and client-rendered apps frequently hide crucial application states and metadata from search engine render queues.",
  points: ["Audit client-side vs server-side hydration bottlenecks", "Ensure clean crawl paths for dynamic software dashboards", "Eliminate duplicate URL parameters and session bloat"]
}, {
  id: "competitive-terms",
  title: "Competitive keyword targeting",
  tag: "Market Positioning",
  icon: Target,
  summary: "Early-stage software companies rarely win head terms immediately. I isolate high-intent buyer queries that enterprise competitors overlook.",
  points: ["Prioritise purchase-ready bottom-funnel queries first", "Deconstruct incumbent software link and content gaps", "Target integration queries (e.g. 'tool + Slack sync')"]
}, {
  id: "lean-acquisition",
  title: "Organic demand alongside paid ads",
  tag: "Channel Strategy",
  icon: Layers,
  summary: "Paid customer acquisition cost rises continuously. I build organic search assets that compound alongside your performance marketing spend.",
  points: ["Repurpose high-performing PPC search queries into organic hubs", "Lower blended customer acquisition cost across cycles", "Build reference libraries that convert trial signups"]
}];
const guidanceSteps = [{
  step: "01",
  title: "Direct consultant audit",
  desc: "You work directly with me. I inspect your application structure, indexing health, and competitor footprint in close collaboration with your founders and product leads."
}, {
  step: "02",
  title: "Search intent blueprint",
  desc: "I outline exact technical fixes, page templates, and content topics that match how engineering buyers and decision-makers actually search."
}, {
  step: "03",
  title: "Pragmatic implementation",
  desc: "I hand your engineers clear, actionable tickets and inspect the deployment in staging before changes ever touch production."
}];
export default function SoftwareSeo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="softwareseoguidance" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground px-3 py-1 font-sans text-xs tracking-wide">
            SaaS & Software Playbook
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight font-default mb-6 leading-tight">
            SEO strategy for software and SaaS teams
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            I help fast-moving software and startup teams turn product
            capabilities into steady, compound organic search demand. You work
            directly with me to solve technical hurdles, capture competitive
            search terms, and reduce reliance on escalating paid spend.
          </p>
        </div>

        {/* Core Strategic Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-20">
          {strategies.map((strat, i) => {
          const IconComp = strat.icon;
          return <div key={strat.id} data-index={i} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <IconComp className="size-5" />
                      </span>
                      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        {strat.tag}
                      </span>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-medium font-default text-card-foreground">
                      {strat.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base mt-2 leading-relaxed">
                      {strat.summary}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <ul className="space-y-2.5">
                      {strat.points.map((point, ptIdx) => <li key={ptIdx} className="flex items-start gap-2.5 text-sm sm:text-base text-foreground/90">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-1" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </div>;
        })}
        </motion.div>

        {/* Consulting Workflow Callout */}
        <div className="rounded-2xl border border-border bg-muted/40 p-8 sm:p-10 lg:p-12 mb-16">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-mono uppercase tracking-wider text-primary font-semibold block mb-2">
              Direct Consultant Model
            </span>
            <h3 className="text-2xl sm:text-3xl font-semibold font-default tracking-tight mb-3">
              How I consult with software founders and product leaders
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              I collaborate directly with founders and product teams from sprint planning to release. By integrating into your existing development cycles, every technical recommendation aligns with your product roadmap and actually gets deployed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {guidanceSteps.map((stepItem, i) => <div key={stepItem.step} data-index={i} className="bg-card border border-border/80 rounded-xl p-6 relative flex flex-col justify-between">
                <div>
                  <span className="text-2xl font-mono font-bold text-accent mb-4 block">
                    {stepItem.step}
                  </span>
                  <h4 className="text-lg font-semibold font-default mb-2 text-card-foreground">
                    {stepItem.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {stepItem.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* CTA & Practical Next Steps */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 rounded-xl border border-border bg-card">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-xl font-semibold font-default text-card-foreground">
              Planning your SaaS organic acquisition strategy?
            </h4>
            <p className="text-sm text-muted-foreground">
              Speak directly with me to review your software’s indexing,
              architecture, and search visibility.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="default" size="lg">
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/TechnicalSeoPage">
                <span>View Technical SEO Services</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}